import React, { createContext, useContext, useState, ReactNode, useEffect, useCallback } from 'react';
import { decryptSharedDesignFromUrlParam } from '../utils/shareLinkCrypto';
import { showToast } from '../components/Toast';
import { buildApiUrl, authHeaders } from '../config/api';
import { renderProduction300DPI } from '../utils/productionRender';

interface DesignElement {
  id: string;
  type: 'tablet' | 'base' | 'text' | 'artwork' | 'image';
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  properties: any;
  flipX?: boolean;
  flipY?: boolean;
  side?: 'front' | 'back';
}

interface DesignDimensions {
  tablet: {
    width: number;
    height: number;
    thickness: number;
    profile: string;
  };
  base: {
    width: number;
    height: number;
    thickness: number;
    profile: string;
  };
}

interface Design {
  id?: string;
  name: string;
  elements: DesignElement[];
  dimensions: DesignDimensions;
  selectedTexture?: any;
  selectedModelId?: number;
  selectedModelName?: string;
  selectedColorId?: number;
  selectedColorIndex?: number;
  selectedColorName?: string;
  selectedSizeId?: number;
  selectedSizeIndex?: number;
  selectedSizeName?: string;
  porcelain_width_in?: number;
  porcelain_height_in?: number;
  porcelain_shape?: string;
  createdAt?: Date;
  updatedAt?: Date;
  activeSide?: 'front' | 'back';
}

interface GraniteMeta {
  modelId: number;
  sizeId: number;
  colorId: number;
  src: string;
}

interface PrintSettings {
  includePrices: boolean;
  includeSizes: boolean;
  includeCompanyLogo: boolean;
  includeManufacturerNote: boolean;
  includeCopyrightNotice: boolean;
  includePurchaseOrder: boolean;
  to: string;
  poNumber: string;
  material: string;
  message: string;
  orientation: 'portrait' | 'landscape';
  textOverlap: boolean;
  textScale: number;
  printBothSides: boolean;
}

interface DesignContextType {
  currentDesign: Design;
  updateDesign: React.Dispatch<React.SetStateAction<Design>>;
  updateDesignNoHistory: React.Dispatch<React.SetStateAction<Design>>;
  lastChangeContext: string;
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  templateResetKey: number;
  forcedGranite?: string;
  incrementTemplateResetKey: () => void;
  canRedo: boolean;
  selectedElement: string | null;
  setSelectedElement: (id: string | null) => void;
  currentTool: string;
  setCurrentTool: (tool: string) => void;
  selectedTexture: any;
  setSelectedTexture: (texture: any) => void;
  saveDesign: () => Promise<void>;
  loadDesign: (id: string) => Promise<void>;
  exportDesign: (format: 'png' | 'pdf', stageRef?: any) => Promise<void>;
  setSelectedModelId: (modelId: number | undefined) => void;
  setSelectedModelName: (name: string | undefined) => void;
  isTemplateSelected: boolean;
  setIsTemplateSelected: (selected: boolean) => void;
  stageRef: React.RefObject<any>;
  setStageRef: (ref: React.RefObject<any>) => void;
  selectedBackground?: { id?: number; name?: string; image_url: string; thumb_url?: string; background_color?: string } | null;
  setSelectedBackground: (bg: { id?: number; name?: string; image_url: string; thumb_url?: string; background_color?: string } | null) => void;
  setTemplateElement: (template: { src: string, width: number, height: number, colorId?: number, isFlatMarker?: boolean, extraProps?: any }) => void;
  selectedGraniteImage: string | null;
  setSelectedGraniteImage: (imageUrl: string | null) => void;
  selectedGraniteBaseImage: string | null;
  setSelectedGraniteBaseImage: (imageUrl: string | null) => void;
  selectedGraniteMeta: GraniteMeta | null;
  setSelectedGraniteMeta: (meta: GraniteMeta | null) => void;
  isApplyingDesign: boolean;
  setIsApplyingDesign: (v: boolean) => void;
  printSettings: PrintSettings;
  setPrintSettings: (settings: PrintSettings) => void;
  layoutGuides: { top: number; bottom: number; left: number; right: number } | null;
  setLayoutGuides: React.Dispatch<React.SetStateAction<{ top: number; bottom: number; left: number; right: number } | null>>;
  isFlatMarkerCurrent: () => boolean;
  isHistoryNavigating?: boolean;
  setIsHistoryNavigating?: (v: boolean) => void;
  isPanelPreloaderActive?: boolean;
  setIsPanelPreloaderActive?: (v: boolean) => void;
  activeSide: 'front' | 'back';
  setActiveSide: (side: 'front' | 'back') => void;
  placementSlots: any[];
  setPlacementSlots: (slots: any[]) => void;
  activePlacementSlot: any | null;
  setActivePlacementSlot: (slot: any | null) => void;
  pixelsPerInch: number;
  setPixelsPerInch: (ppi: number) => void;
  renderedTemplateOrigin: { x: number; y: number; width: number; height: number } | null;
  setRenderedTemplateOrigin: (origin: { x: number; y: number; width: number; height: number } | null) => void;
  sampleGuideLines: { visible: boolean; h1: number; h2: number; v1: number; v2: number };
  setSampleGuideLines: React.Dispatch<React.SetStateAction<{ visible: boolean; h1: number; h2: number; v1: number; v2: number }>>;
  designMode: 'LSP/QD' | 'ESQ';
  setDesignMode: (mode: 'LSP/QD' | 'ESQ', skipReset?: boolean) => void;
  // Global Shared Collections
  sharedModels: any[];
  sharedColors: any[];
  loadingMessage: string;
  setLoadingMessage: React.Dispatch<React.SetStateAction<string>>;
  sharedCategories: any[];
  sharedFonts: any[];
  isGlobalLoading: boolean;
  setIsGlobalLoading: (v: boolean) => void;
  textStyles: any;
  isLoadingTextStyles: boolean;
  openModal: string | null;
  setOpenModal: (modal: string | null) => void;
  showPersonalTextOnCanvas: boolean;
  setShowPersonalTextOnCanvas: (show: boolean) => void;
  authedUser: any | null;
  isDealerSalesperson: boolean;
  canViewPrice: boolean;
  canCreateQuotation: boolean;
  areTextAssetsLoaded: boolean;
  setAuthedUser: (user: any | null) => void;
  loadTextAssets: () => Promise<void>;
  loadArtworkCategories: () => Promise<void>;
  loadShapesData: () => Promise<void>;
  fetchModelDetails: (modelId: number, colorId?: number) => Promise<any>;
  fetchDesignPreview: (modelId: number, sizeId: number, colorId: number) => Promise<any>;
}

const DesignContext = createContext<DesignContextType | undefined>(undefined);

const defaultDesign: Design = {
  name: 'New Design',
  elements: [],
  // Initial model/size are now handled by the auto-initializer useEffect after boot.
  selectedModelId: undefined,
  selectedSizeName: undefined,
  activeSide: 'front',
  dimensions: {
    tablet: {
      width: 24,
      height: 24,
      thickness: 6,
      profile: 'P5'
    },
    base: {
      width: 36,
      height: 12,
      thickness: 6,
      profile: 'PT'
    }
  }
};

const getBackgroundStorageKey = (mode: 'LSP/QD' | 'ESQ') =>
  mode === 'ESQ' ? 'design_tool.background.esq' : 'design_tool.background.lsb';

const parseBackground = (raw: string | null) => {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

export const DesignProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentDesign, setCurrentDesign] = useState<Design>(defaultDesign);
  // History stacks
  const [past, setPast] = useState<Design[]>([]);
  const [future, setFuture] = useState<Design[]>([]);
  const [lastChangeContext, setLastChangeContext] = useState<string>('history');
  // Keep a ref to the latest currentDesign to avoid stale captures during undo/redo
  const currentDesignRef = React.useRef<Design>(currentDesign);
  const [selectedElement, setSelectedElement] = useState<string | null>(null);
  const [currentTool, setCurrentTool] = useState<string>('art');
  const [selectedTexture, setSelectedTexture] = useState<any>(null);
  const [isTemplateSelectedState, setIsTemplateSelectedState] = useState<boolean>(false);
  const [stageRef, setStageRef] = useState<React.RefObject<any>>(React.createRef());
  const [selectedBackground, setSelectedBackground] = useState<{ id?: number; name?: string; image_url: string; thumb_url?: string; background_color?: string } | null>(() => {
    try {
      const pageMode = sessionStorage.getItem('design_tool.page_mode');
      const effectiveMode: 'LSP/QD' | 'ESQ' = (pageMode === 'ESQ' || pageMode === 'LSP/QD')
        ? pageMode
        : ((localStorage.getItem('design_tool.mode') === 'ESQ') ? 'ESQ' : 'LSP/QD');

      const modeSaved = parseBackground(localStorage.getItem(getBackgroundStorageKey(effectiveMode)));
      if (modeSaved) return modeSaved;
    } catch { }
    return null;
  });
  const [templateResetKey, setTemplateResetKey] = useState<number>(0);
  const [selectedGraniteImage, setSelectedGraniteImage] = useState<string | null>(null);
  const [selectedGraniteBaseImage, setSelectedGraniteBaseImage] = useState<string | null>(null);
  const [selectedGraniteMeta, setSelectedGraniteMeta] = useState<GraniteMeta | null>(null);
  const [isApplyingDesign, setIsApplyingDesign] = useState<boolean>(() => {
    try {
      const u = new URL(window.location.href);
      return !!(u.searchParams.get('d') || u.searchParams.get('sid'));
    } catch { return false; }
  });
  const [printSettings, setPrintSettings] = useState<PrintSettings>({
    includePrices: false,
    includeSizes: true,
    includeCompanyLogo: true,
    includeManufacturerNote: true,
    includeCopyrightNotice: true,
    includePurchaseOrder: true,
    to: '',
    poNumber: '',
    material: 'Medium Gray',
    message: '',
    orientation: 'landscape',
    textOverlap: true,
    textScale: 1.5,
    printBothSides: true
  });
  const [layoutGuides, setLayoutGuides] = useState<{ top: number; bottom: number; left: number; right: number } | null>(null);
  const [isHistoryNavigating, setIsHistoryNavigating] = useState<boolean>(false);
  const [isPanelPreloaderActive, setIsPanelPreloaderActive] = useState<boolean>(false);
  const historyNavTimerRef = React.useRef<number | null>(null);
  const [placementSlots, setPlacementSlots] = useState<any[]>([]);
  const [activePlacementSlot, setActivePlacementSlot] = useState<any | null>(null);
  const [loadingMessage, setLoadingMessage] = useState<string>('Loading...');
  const [pixelsPerInch, setPixelsPerInch] = useState<number>(35);
  const [renderedTemplateOrigin, setRenderedTemplateOrigin] = useState<{ x: number; y: number; width: number; height: number } | null>(null);
  const [designMode, setDesignMode] = useState<'LSP/QD' | 'ESQ'>(() => {
    try {
      const pageMode = sessionStorage.getItem('design_tool.page_mode');
      if (pageMode === 'ESQ' || pageMode === 'LSP/QD') return pageMode;
      const saved = localStorage.getItem('design_tool.mode');
      return (saved === 'ESQ') ? 'ESQ' : 'LSP/QD';
    } catch { return 'LSP/QD'; }
  });
  const [sampleGuideLines, setSampleGuideLines] = useState<{ visible: boolean; h1: number; h2: number; v1: number; v2: number }>({
    visible: false,
    h1: 1.5,
    h2: 4,
    v1: 1.5,
    v2: 5.5
  });

  const updateDesignMode = (mode: 'LSP/QD' | 'ESQ', skipReset: boolean = false) => {
    setDesignMode(mode);
    try { localStorage.setItem('design_tool.mode', mode); } catch { }
    try { sessionStorage.setItem('design_tool.page_mode', mode); } catch { }

    if (mode === 'ESQ') {
      try { updateWithoutHistory((prev: any) => ({ ...prev, activeSide: 'front' })); } catch { }
    }

    if (!skipReset && !isApplyingDesign && !(window as any).__isApplyingDesign) {
      // When user manually switches modes, we reset the design. 
      resetToDefault();

      // Flag to the UI that we need to re-initialize defaults
      try { (window as any).__initialDesignApplied = false; } catch { }
    } else {
      try { (window as any).__initialDesignApplied = true; } catch { }
    }
  };

  // Keep LSP/QD and ESQ backgrounds isolated from each other.
  React.useEffect(() => {
    if (isApplyingDesign || (window as any).__isApplyingDesign) return;
    try {
      const modeSaved = parseBackground(localStorage.getItem(getBackgroundStorageKey(designMode)));
      if (modeSaved) {
        setSelectedBackground(modeSaved);
      } else {
        setSelectedBackground(null);
      }
    } catch {
      setSelectedBackground(null);
    }
  }, [designMode]);

  // Persist background to mode-specific key and legacy key.
  React.useEffect(() => {
    try {
      const key = getBackgroundStorageKey(designMode);
      if (selectedBackground) {
        localStorage.setItem(key, JSON.stringify(selectedBackground));
      } else {
        localStorage.removeItem(key);
      }
    } catch { }
  }, [designMode, selectedBackground]);

  const resetToDefault = () => {
    const activeColorId = currentDesignRef.current?.selectedColorId;
    const activeColorName = currentDesignRef.current?.selectedColorName;
    const activeColorIndex = currentDesignRef.current?.selectedColorIndex;

    setCurrentDesign({
      ...defaultDesign,
      ...(activeColorId ? {
        selectedColorId: activeColorId,
        selectedColorName: activeColorName,
        selectedColorIndex: activeColorIndex
      } : {})
    });
    setPast([]);
    setFuture([]);
    setSelectedElement(null);
    setSelectedGraniteImage(null);
    setSelectedGraniteBaseImage(null);
    setSelectedGraniteMeta(null);
    setTemplateResetKey(prev => prev + 1);
  };

  // Global shared collections
  const [sharedModels, setSharedModels] = useState<any[]>([]);
  const [sharedColors, setSharedColors] = useState<any[]>([]);
  const [sharedCategories, setSharedCategories] = useState<any[]>([]);
  const [sharedFonts, setSharedFonts] = useState<any[]>([]);
  const [isGlobalLoading, setIsGlobalLoading] = useState<boolean>(true);
  const [textStyles, setTextStyles] = useState<any>(null);
  const [isLoadingTextStyles, setIsLoadingTextStyles] = useState<boolean>(false);
  const [areTextAssetsLoaded, setAreTextAssetsLoaded] = useState<boolean>(false);
  const [openModal, setOpenModal] = useState<string | null>(null);
  const [showPersonalTextOnCanvas, setShowPersonalTextOnCanvas] = useState<boolean>(true);
  const [authedUser, setAuthedUser] = useState<any | null>(() => {
    try {
      const userStr = localStorage.getItem('auth_user') || sessionStorage.getItem('auth_user');
      return userStr ? JSON.parse(userStr) : null;
    } catch { return null; }
  });

  React.useEffect(() => {
    const handleAuthChange = (e: any) => {
      try {
        if (e?.detail) {
          setAuthedUser(e.detail);
          return;
        }
        const userStr = localStorage.getItem('auth_user') || sessionStorage.getItem('auth_user');
        setAuthedUser(userStr ? JSON.parse(userStr) : null);
      } catch { }
    };

    window.addEventListener('auth:user_updated', handleAuthChange);
    window.addEventListener('auth:login', handleAuthChange);
    window.addEventListener('auth:logout', handleAuthChange);

    return () => {
      window.removeEventListener('auth:user_updated', handleAuthChange);
      window.removeEventListener('auth:login', handleAuthChange);
      window.removeEventListener('auth:logout', handleAuthChange);
    };
  }, []);

  const modelDetailsCacheRef = React.useRef<Record<string, any>>({});
  const inFlightModelDetails = React.useRef<Record<string, Promise<any>>>({});
  const previewCacheRef = React.useRef<Record<string, any>>({});
  const inFlightPreview = React.useRef<Record<string, Promise<any>>>({});
  // Track which modelIds have had their background fetched to prevent double-fetch (Strict Mode)
  const backgroundFetchedForModelRef = React.useRef<Record<number, boolean>>({});
  const backgroundCacheRef = React.useRef<Record<string, any[]>>({});
  const inFlightBackgrounds = React.useRef<Record<string, Promise<any[]> | undefined>>({});

  const fetchModelDetails = useCallback(async (modelId: number, colorId?: number) => {
    const cacheKey = colorId ? `${modelId}_${colorId}` : `${modelId}_all`;

    if (modelDetailsCacheRef.current[cacheKey]) {
      return modelDetailsCacheRef.current[cacheKey];
    }

    if (inFlightModelDetails.current[cacheKey] !== undefined) {
      return inFlightModelDetails.current[cacheKey];
    }

    const promise = (async () => {
      try {
        const url = colorId
          ? buildApiUrl(`/api/models/${modelId}/details?color_id=${colorId}`)
          : buildApiUrl(`/api/models/${modelId}/details`);

        const res = await fetch(url);
        if (!res.ok) throw new Error(`Details fetch failed: ${res.status}`);
        const data = await res.json();

        modelDetailsCacheRef.current[cacheKey] = data;
        return data;
      } finally {
        delete inFlightModelDetails.current[cacheKey];
      }
    })();

    inFlightModelDetails.current[cacheKey] = promise;
    return promise;
  }, []);

  // Shared design preview fetcher with deduplication and caching
  // Prevents Designer + LeftPanel issuing parallel calls for the same preview endpoint
  const fetchDesignPreview = useCallback(async (modelId: number, sizeId: number, colorId: number) => {
    const cacheKey = `${modelId}_${sizeId}_${colorId}`;

    if (previewCacheRef.current[cacheKey]) {
      return previewCacheRef.current[cacheKey];
    }

    if (inFlightPreview.current[cacheKey] !== undefined) {
      return inFlightPreview.current[cacheKey];
    }

    const promise = (async () => {
      try {
        const url = buildApiUrl(`/api/designs/preview?model_id=${modelId}&size_id=${sizeId}&color_id=${colorId}`);
        const res = await fetch(url);
        if (!res.ok) throw new Error(`Preview fetch failed: ${res.status}`);
        const data = await res.json();
        previewCacheRef.current[cacheKey] = data;
        return data;
      } finally {
        delete inFlightPreview.current[cacheKey];
      }
    })();

    inFlightPreview.current[cacheKey] = promise;
    return promise;
  }, []);

  // Keep authedUser in sync with events from other components (like Toolbar)
  React.useEffect(() => {
    const syncAuth = () => {
      try {
        const userStr = localStorage.getItem('auth_user') || sessionStorage.getItem('auth_user');
        setAuthedUser(userStr ? JSON.parse(userStr) : null);
      } catch { setAuthedUser(null); }
    };
    window.addEventListener('auth:login', syncAuth);
    window.addEventListener('auth:logout', syncAuth);
    window.addEventListener('storage', syncAuth); // Also sync across tabs
    return () => {
      window.removeEventListener('auth:login', syncAuth);
      window.removeEventListener('auth:logout', syncAuth);
      window.removeEventListener('storage', syncAuth);
    };
  }, []);

  // Ref to track if fetch has already run (prevents double-fetch in Strict Mode)
  const globalFetchRan = React.useRef(false);

  // Initial load of global collections
  React.useEffect(() => {
    if (globalFetchRan.current) return;
    globalFetchRan.current = true;

    const fetchGlobalData = async () => {
      setIsGlobalLoading(true);

      // Fetch colors independently
      fetch(buildApiUrl('/api/colors'))
        .then(res => res.ok ? res.json() : { colors: [] })
        .then(data => {
          const arr = Array.isArray(data.colors) ? data.colors : (Array.isArray(data) ? data : []);
          setSharedColors(arr);
        })
        .catch(e => console.error('Failed to fetch colors:', e))
        .finally(() => {
          setIsGlobalLoading(false);
        });
    };

    fetchGlobalData();
  }, []);

  // Track the last (sizeId, colorCode) combination for which text styles were loaded,
  // so we can detect when a model-size or color change requires a re-fetch.
  const lastTextStyleKeyRef = React.useRef<string>('');

  // On-demand loader for text assets (fonts and text-styles).
  // Re-fetches automatically when the selected model size or granite color changes.
  const loadTextAssets = useCallback(async () => {
    // Use the stored colorId for a stable ID-based lookup instead of an array-index
    // lookup. The Colors panel sorts displayColors differently from sharedColors, so
    // selectedColorIndex is a sorted-display position, NOT a sharedColors array index.
    // Using the ID avoids PJB/MG cross-contamination when colors are reordered.
    const colorId = (currentDesignRef.current as any).selectedColorId;
    const current = sharedColors.find((c: any) => Number(c.id) === Number(colorId)) || {};
    const colorCode = (current as any)?.code || 'PJB';
    const sizeId = currentDesignRef.current.selectedSizeId || 0;

    // Build a cache key — re-fetch whenever the key changes
    const cacheKey = `${colorCode}:${sizeId}`;

    // 1. If everything is already loaded for this key, bail
    if (areTextAssetsLoaded && textStyles && lastTextStyleKeyRef.current === cacheKey) return;

    // 2. If size is missing but fonts are already loaded, we can't do anything more yet.
    // We must wait for a non-zero sizeId to fetch the text styles.
    if (sizeId <= 0 && areTextAssetsLoaded) return;

    try {
      setIsLoadingTextStyles(true);

      const fetchFonts = !areTextAssetsLoaded;
      const fetchStyles = sizeId > 0;

      // If we have nothing to fetch, bail (should be handled by checks above, but safety first)
      if (!fetchFonts && !fetchStyles) return;

      const requests: Promise<Response>[] = [];
      if (fetchFonts) requests.push(fetch(buildApiUrl('/api/fonts')));
      if (fetchStyles) requests.push(fetch(buildApiUrl(`/api/text-styles/${colorCode}?size_id=${sizeId}`)));

      const responses = await Promise.all(requests);

      let resIdx = 0;
      if (fetchFonts) {
        const fontsRes = responses[resIdx++];
        if (fontsRes && fontsRes.ok) {
          const data = await fontsRes.json();
          const arr = Array.isArray(data?.data?.fonts) ? data.data.fonts : (Array.isArray(data?.fonts) ? data.fonts : []);
          setSharedFonts(arr);
        }
      }

      if (fetchStyles) {
        const stylesRes = responses[resIdx++];
        if (stylesRes && stylesRes.ok) {
          const data = await stylesRes.json();
          if (data.status === 'success') {
            setTextStyles(data.data);
          }
        }
      }

      // Only update the last-loaded key if we actually fetched styles.
      // If we only fetched fonts, we still need to fetch styles once sizeId is available.
      if (fetchStyles) {
        lastTextStyleKeyRef.current = cacheKey;
      }
      setAreTextAssetsLoaded(true);
    } catch (error) {
      console.error('Failed to load text assets:', error);
    } finally {
      setIsLoadingTextStyles(false);
    }
  }, [areTextAssetsLoaded, textStyles, sharedColors]);

  // On-demand loader for text assets (fonts and text-styles) removed automatic trigger
  // to prioritize initial load speed as per user request.

  // Load shared design from ?d=..., ?sid=..., or existing design from ?loadDesignId=...
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const u = new URL(window.location.href);
        const token = u.searchParams.get('d');
        const sid = u.searchParams.get('sid');
        const loadId = u.searchParams.get('loadDesignId');

        if (!token && !sid && !loadId) return;

        // Prevent intermediate UI flashes while applying a large snapshot
        try { setIsApplyingDesign(true); } catch { }
        try { (window as any).__isApplyingDesign = true; } catch { }
        try {
          setSelectedGraniteImage(null);
          setSelectedGraniteMeta(null);
          setTemplateResetKey(prev => prev + 1);
        } catch { }

        let parsed: any = null;
        let designId: string | undefined = undefined;

        let isQuotedFromBackend = false;
        let rawDesignRow: any = null;

        if (token) {
          parsed = await decryptSharedDesignFromUrlParam(token);
        } else if (sid) {
          const res = await fetch(buildApiUrl(`/api/share/${encodeURIComponent(String(sid))}`), {
            headers: { 'Accept': 'application/json' },
          });
          const j = await res.json().catch(() => ({}));
          if (!res.ok || !j?.ok) {
            throw new Error(j?.error || 'Failed to load shared design');
          }
          parsed = j?.payload;
        } else if (loadId) {
          let res = await fetch(buildApiUrl(`/api/designs/${loadId}`), {
            headers: { ...authHeaders() }
          });
          if (!res.ok) {
            const adminToken = localStorage.getItem('admin_token') || sessionStorage.getItem('admin_token');
            if (adminToken) {
              res = await fetch(buildApiUrl(`/api/admin/designs/${loadId}`), {
                headers: { Authorization: `Bearer ${adminToken}` }
              });
            }
          }
          const j = await res.json().catch(() => ({}));
          if (res.ok && j?.ok) {
            designId = String(loadId);
            rawDesignRow = j?.design || {};
            parsed = rawDesignRow;
            if (rawDesignRow.status === 'quoted' || Boolean(rawDesignRow.is_quoted)) {
              isQuotedFromBackend = true;
            }
            // Parse internal design_json or design_data
            if (parsed && typeof parsed.design_json === 'string') {
              try { parsed = JSON.parse(parsed.design_json); } catch { }
            } else if (parsed && typeof parsed.design_data === 'string') {
              try { parsed = JSON.parse(parsed.design_data); } catch { }
            }
          } else {
            throw new Error(j?.error || 'Failed to load design');
          }
        }

        const designFromPayload = (parsed && (parsed.design || parsed.data || parsed)) as any;
        if (!designFromPayload || typeof designFromPayload !== 'object') {
          throw new Error('Invalid design payload');
        }

        if (cancelled) return;

        const normalizeSharedDesign = (incoming: any) => {
          try {
            const elements = Array.isArray(incoming?.elements) ? incoming.elements : [];
            const templateEl = elements.find((el: any) => el?.type === 'artwork' && (el?.properties?.role === 'template' || el?.properties?.isTemplate));
            const tplCenterX = templateEl
              ? ((Number(templateEl.x) || 0) + (Number(templateEl.width) || 0) / 2)
              : null;

            const normalizedElements = elements.map((el: any) => {
              const role = String(el?.properties?.role || el?.properties?.kind || '').toLowerCase();
              const isVase = el?.type === 'artwork' && role === 'vase';

              let inferredSide: 'left' | 'right' | undefined;
              if (isVase && tplCenterX !== null && el?.properties?.side == null) {
                const vaseCenter = (Number(el.x) || 0) + (Number(el.width) || 0) / 2;
                inferredSide = vaseCenter >= tplCenterX ? 'right' : 'left';
              }

              const normalizedEl: any = {
                ...el,
                p_id: el.id, // preserve original id potentially
                x: typeof el.x === 'number' ? el.x : 0,
                y: typeof el.y === 'number' ? el.y : 0,
                width: typeof el.width === 'number' && el.width > 0 ? el.width : (el.width || 0),
                height: typeof el.height === 'number' && el.height > 0 ? el.height : (el.height || 0),
                rotation: typeof el.rotation === 'number' ? el.rotation : 0,
                flipX: el.flipX === true,
                flipY: el.flipY === true,
                properties: {
                  ...el.properties,
                  ...(inferredSide ? { side: inferredSide } : {}),
                  fromSaved: true,
                  userResized: true,
                  autosnap: isVase ? true : el?.properties?.autosnap,
                }
              };

              if (el?.type === 'text') {
                normalizedEl.boxWidth = typeof (el as any).boxWidth === 'number' ? (el as any).boxWidth : el.width;
              }

              return normalizedEl;
            });

            return {
              ...incoming,
              elements: normalizedElements,
            };
          } catch {
            return incoming;
          }
        };

        const normalizedShared = normalizeSharedDesign(designFromPayload);

        const inferredMode: 'LSP/QD' | 'ESQ' = (() => {
          const topMode = String((normalizedShared as any)?.designMode || (normalizedShared as any)?.mode || '').toUpperCase();
          if (topMode === 'ESQ') return 'ESQ';
          if (topMode === 'LSP/QD' || topMode === 'LSP' || topMode === 'LSP-QD') return 'LSP/QD';
          if (Number((normalizedShared as any)?.selectedEsqModalId || 0) > 0) return 'ESQ';
          const els = Array.isArray((normalizedShared as any)?.elements) ? (normalizedShared as any).elements : [];
          for (const el of els) {
            const p = (el && el.properties) || {};
            const m = String(p?.designMode || '').toUpperCase();
            if (m === 'ESQ' || Number(p?.esqModalId || 0) > 0 || String(p?.esqModalName || '').trim()) return 'ESQ';
          }
          return 'LSP/QD';
        })();

        updateDesignMode(inferredMode, true);

        // Apply without affecting undo/redo history stacks
        try {
          setPast([]);
          setFuture([]);
        } catch { }

        updateWithoutHistory((prev: any) => {
          const isQuoted = Boolean(isQuotedFromBackend || normalizedShared?.status === 'quoted' || normalizedShared?.is_quoted);
          const next: any = {
            ...prev,
            ...normalizedShared,
            activeSide: 'front',
            id: designId || normalizedShared.id, // Do not fallback to prev.id when loading new payload
            is_quoted: isQuoted,
            status: isQuoted ? 'quoted' : ((rawDesignRow && rawDesignRow.status) || normalizedShared?.status || 'saved'),
          };

          // Ensure special fields are also synchronized
          try {
            if (normalizedShared.background) {
              next.background = normalizedShared.background;
            }
          } catch { }

          return next;
        });

        await new Promise(resolve => setTimeout(resolve, 50));

        // Sync auxiliary context state
        try {
          if (normalizedShared.background) {
            let bg = normalizedShared.background;
            if (typeof bg === 'string') {
              bg = { image_url: bg, name: 'Background' };
            } else if (bg?.src && !bg.image_url) {
              bg = { ...bg, image_url: bg.src };
            }
            setSelectedBackground(bg);
            const key = getBackgroundStorageKey(inferredMode);
            try {
              localStorage.setItem(key, JSON.stringify(bg));
              localStorage.setItem('design_tool.background', JSON.stringify(bg));
            } catch { }
          } else {
            setSelectedBackground(null);
          }
        } catch { }
        try {
          const els = Array.isArray(normalizedShared?.elements) ? normalizedShared.elements : [];
          const tmpl = els.find((el: any) => el?.type === 'artwork' && (el?.properties?.role === 'template' || el?.properties?.isTemplate));
          const meta = normalizedShared.selectedGraniteMeta || (tmpl && tmpl.properties && tmpl.properties.src ? {
            modelId: tmpl.properties.modelId || 0,
            sizeId: tmpl.properties.sizeId || 0,
            colorId: tmpl.properties.colorId || 0,
            src: tmpl.properties.src
          } : null);

          if (meta) {
            setSelectedGraniteMeta(meta);
            if (meta.src) {
              setSelectedGraniteImage(meta.src);
            }
          } else {
            setSelectedGraniteMeta(null);
            setSelectedGraniteImage(null);
          }
        } catch { }
        try {
          const elements = Array.isArray(normalizedShared?.elements) ? normalizedShared.elements : [];
          const savedBase = normalizedShared.selectedGraniteBaseImage;
          if (savedBase) {
            setSelectedGraniteBaseImage(savedBase);
          } else if (inferredMode === 'ESQ') {
            const modalId = Number(
              normalizedShared.selectedEsqModalId ||
              normalizedShared.selectedModelId ||
              elements.find((el: any) => el?.properties?.esqModalId || el?.properties?.modalId)?.properties?.esqModalId ||
              0
            );
            if (modalId > 0) {
              fetch(buildApiUrl(`/api/esq/modal/${modalId}`))
                .then(res => res.ok ? res.json() : null)
                .then(modal => {
                  if (modal && modal.base_path) {
                    setSelectedGraniteBaseImage(buildApiUrl('/api/uploads/' + String(modal.base_path).replace(/^\/?uploads\//, '')));
                  } else {
                    setSelectedGraniteBaseImage(null);
                  }
                })
                .catch(() => setSelectedGraniteBaseImage(null));
            } else {
              setSelectedGraniteBaseImage(null);
            }
          } else {
            setSelectedGraniteBaseImage(null);
          }
        } catch { }

        // Remove token/loadId from URL so refresh doesn't re-apply repeatedly
        try {
          const clean = new URL(window.location.href);
          clean.searchParams.delete('d');
          clean.searchParams.delete('sid');
          clean.searchParams.delete('loadDesignId');
          window.history.replaceState({}, '', clean.toString());
        } catch { }

        try { showToast(loadId ? 'Design loaded' : 'Shared design loaded', { kind: 'success', title: loadId ? 'Load' : 'Share' }); } catch { }
      } catch (e: any) {
        try { showToast(e?.message || 'Failed to load design', { kind: 'error', title: 'Load' }); } catch { }
      } finally {
        // Don't clear isApplyingDesign here - let Designer's asset tracking clear it
        // once all images are loaded to prevent empty background flash
        try { (window as any).__isApplyingDesign = false; } catch { }
      }
    })();
    return () => { cancelled = true; };
    // Run once on app boot
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Automatically update background when model changes
  React.useEffect(() => {
    const modelId = currentDesign?.selectedModelId;
    const sizeId = currentDesign?.selectedSizeId;
    const colorId = currentDesign?.selectedColorId;
    if (!modelId) return;

    // Do NOT auto-clear or overwrite background when loading/applying a saved design
    if (isApplyingDesign || (window as any).__isApplyingDesign) return;

    // Use designMode in cacheKey to prevent ESQ modal IDs matching regular model IDs
    const cacheKey = `${designMode}_${modelId}_${sizeId || 0}_${colorId || 0}`;

    // If we have a cached result, apply it immediately without fetching again
    if (backgroundCacheRef.current[cacheKey]) {
      const list = backgroundCacheRef.current[cacheKey];
      if (list.length > 0) {
        const newBg = list[0];
        setSelectedBackground(prev => {
          if (!prev || prev.image_url !== newBg.image_url) {
            try { localStorage.setItem('design_tool.background', JSON.stringify(newBg)); } catch { }
            return newBg;
          }
          return prev;
        });
      }
      return;
    }

    // Clear background immediately when switching models/sizes/colors
    setSelectedBackground(null);

    const controller = new AbortController();
    const timer = setTimeout(() => {
      // Deduplicate: if an identical request is in-flight, wait for it
      const inFlight = inFlightBackgrounds.current[cacheKey];
      if (inFlight) {
        inFlight.then(list => {
          if (controller.signal.aborted) return;
          if (list.length > 0) {
            const newBg = list[0];
            setSelectedBackground(prev => {
              if (!prev || prev.image_url !== newBg.image_url) {
                try { localStorage.setItem('design_tool.background', JSON.stringify(newBg)); } catch { }
                return newBg;
              }
              return prev;
            });
          }
        }).catch(() => { });
        return;
      }

      const promise = (async (): Promise<any[]> => {
        try {
          const res = await fetch(buildApiUrl(`/api/backgrounds?modelId=${modelId}&sizeId=${sizeId || 0}&colorId=${colorId || 0}&designMode=${designMode}`), {
            signal: controller.signal
          });
          const data = await res.json();
          const list = Array.isArray(data?.backgrounds) ? data.backgrounds : [];
          backgroundCacheRef.current[cacheKey] = list;
          return list;
        } catch (e) {
          if (e instanceof Error && e.name === 'AbortError') return [];
          return [];
        } finally {
          delete inFlightBackgrounds.current[cacheKey];
        }
      })();

      inFlightBackgrounds.current[cacheKey] = promise;

      promise.then(list => {
        if (controller.signal.aborted) return;
        if (list.length > 0) {
          const newBg = list[0];
          setSelectedBackground(prev => {
            if (!prev || prev.image_url !== newBg.image_url) {
              try { localStorage.setItem('design_tool.background', JSON.stringify(newBg)); } catch { }
              return newBg;
            }
            return prev;
          });
        }
      }).catch(() => { });
    }, 100);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [currentDesign?.selectedModelId, currentDesign?.selectedSizeId, currentDesign?.selectedColorId, designMode]);

  // Re-fetch text styles whenever the selected model size or granite color changes
  // so the correct per-size font/size rules from model_size_text_styles are always active.
  const prevTextStyleKeyRef = React.useRef<string>('');
  React.useEffect(() => {
    const colorId = currentDesign?.selectedColorId;
    const colorCode = (sharedColors.find((c: any) => Number(c?.id) === Number(colorId)) as any)?.code || '';
    const sizeId = currentDesign?.selectedSizeId || 0;
    if (!colorCode) return;

    const key = `${colorCode}:${sizeId}`;
    if (key === prevTextStyleKeyRef.current) return;
    prevTextStyleKeyRef.current = key;

    // Invalidate the loaded flag so loadTextAssets will re-fetch for the new key
    setAreTextAssetsLoaded(false);
  }, [currentDesign?.selectedSizeId, currentDesign?.selectedColorId, sharedColors]);

  React.useEffect(() => {
    // Always mirror the latest currentDesign into the ref
    currentDesignRef.current = currentDesign;
  }, [currentDesign]);

  // Deep clone utility with structuredClone fallback
  const deepClone = <T,>(obj: T): T => {
    try {
      // @ts-ignore structuredClone in modern browsers
      if (typeof structuredClone === 'function') return structuredClone(obj);
    } catch { }
    return JSON.parse(JSON.stringify(obj));
  };

  const HISTORY_LIMIT = 100;

  // Stable stringify to detect duplicates
  const stableStringify = (v: any) => {
    try { return JSON.stringify(v); } catch { return String(v); }
  };

  const shouldPushSnapshot = (prev: Design, next: Design) => {
    return stableStringify(prev) !== stableStringify(next);
  };

  const isTemplateLikeElement = (el: any) => {
    try {
      if (!el) return false;
      if (el.type === 'tablet' || el.type === 'base') return true;
      if (el.type !== 'artwork') return false;
      const role = String(el.properties?.role || '').toLowerCase();
      return el.properties?.isTemplate === true || role === 'template';
    } catch {
      return false;
    }
  };

  const mergeSnapshotPreservingModel = (snapshot: Design, current: Design): Design => {
    const currentElements = Array.isArray(current?.elements) ? current.elements : [];
    const snapshotElements = Array.isArray(snapshot?.elements) ? snapshot.elements : [];

    const preservedModelElements = currentElements.filter(isTemplateLikeElement);
    const restoredNonModelElements = snapshotElements.filter((el: any) => !isTemplateLikeElement(el));

    return {
      ...snapshot,
      // Preserve model/size selections from the current design so undo/redo never changes the model.
      selectedModelId: (current as any)?.selectedModelId,
      selectedSizeId: (current as any)?.selectedSizeId,
      selectedSizeIndex: (current as any)?.selectedSizeIndex,
      selectedSizeName: (current as any)?.selectedSizeName,
      // Preserve current template/tablet/base elements
      elements: [...preservedModelElements, ...restoredNonModelElements]
    } as Design;
  };

  const stripModelParts = (d: Design): any => {
    try {
      const anyD: any = d as any;
      const {
        selectedModelId,
        selectedSizeId,
        selectedSizeIndex,
        selectedSizeName,
        selectedColorId,
        selectedColorIndex,
        selectedColorName,
        dimensions,
        ...rest
      } = anyD || {};

      return {
        ...rest,
        elements: (Array.isArray(anyD?.elements) ? anyD.elements : []).filter((el: any) => !isTemplateLikeElement(el))
      };
    } catch {
      return d;
    }
  };

  const isModelOnlyChange = (prev: Design, next: Design) => {
    try {
      return stableStringify(stripModelParts(prev)) === stableStringify(stripModelParts(next));
    } catch {
      return false;
    }
  };

  // Wrap state updater to record history and enforce artwork compatibility
  const updateWithHistory: React.Dispatch<React.SetStateAction<Design>> = (updater) => {
    setCurrentDesign(prev => {
      const prevSnapshot: Design = JSON.parse(JSON.stringify(prev));
      let next = typeof updater === 'function' ? (updater as (d: Design) => Design)(prev) : updater;

      // If no change, don't push history
      if (!shouldPushSnapshot(prev, next)) return prev;

      // Model-only changes (template/tablet/base + model/size/color fields) should not be undoable.
      // This prevents initial model load / model changes from enabling Undo.
      if (isModelOnlyChange(prev, next)) {
        try { setLastChangeContext('model'); } catch { }
        return next;
      }

      try { setLastChangeContext('history'); } catch { }
      setPast(p => {
        const stack = [...p, prevSnapshot];
        // Limit history depth
        return stack.length > HISTORY_LIMIT ? stack.slice(stack.length - HISTORY_LIMIT) : stack;
      });

      setFuture([]); // clear redo stack on new change
      return next;
    });
  };

  // State updater that enforces compatibility but does NOT record undo/redo history
  const updateWithoutHistory: React.Dispatch<React.SetStateAction<Design>> = (updater) => {
    setCurrentDesign(prev => {
      let next = typeof updater === 'function' ? (updater as (d: Design) => Design)(prev) : updater;

      if (!shouldPushSnapshot(prev, next)) return prev;
      try { setLastChangeContext('model'); } catch { }
      return next;
    });
  };

  const undo = () => {
    try { setLastChangeContext('history'); } catch { }
    // Begin history navigation and hold a short cooldown to suppress preloaders
    setIsHistoryNavigating(true);
    if (historyNavTimerRef.current) { try { window.clearTimeout(historyNavTimerRef.current); } catch { } }
    setPast(p => {
      if (!p.length) return p;
      const prev = p[p.length - 1];
      const preserved = mergeSnapshotPreservingModel(prev, currentDesignRef.current);
      // push current into future
      setFuture(f => {
        const nextFuture = [...f, deepClone(currentDesignRef.current)];
        return nextFuture.length > HISTORY_LIMIT ? nextFuture.slice(nextFuture.length - HISTORY_LIMIT) : nextFuture;
      });
      setCurrentDesign(preserved);
      return p.slice(0, -1);
    });
    // Keep suppression active briefly to allow images/layout to settle without flashing overlay
    historyNavTimerRef.current = window.setTimeout(() => {
      setIsHistoryNavigating(false);
      historyNavTimerRef.current = null;
    }, 1200);
  };

  const redo = () => {
    try { setLastChangeContext('history'); } catch { }
    setIsHistoryNavigating(true);
    if (historyNavTimerRef.current) { try { window.clearTimeout(historyNavTimerRef.current); } catch { } }
    setFuture(f => {
      if (!f.length) return f;
      const next = f[f.length - 1];
      const preserved = mergeSnapshotPreservingModel(next, currentDesignRef.current);
      // push current into past
      setPast(p => {
        const nextPast = [...p, deepClone(currentDesignRef.current)];
        return nextPast.length > HISTORY_LIMIT ? nextPast.slice(nextPast.length - HISTORY_LIMIT) : nextPast;
      });
      setCurrentDesign(preserved);
      return f.slice(0, -1);
    });
    historyNavTimerRef.current = window.setTimeout(() => {
      setIsHistoryNavigating(false);
      historyNavTimerRef.current = null;
    }, 1200);
  };

  const saveDesign = async () => {
    try {
      // API call to save design

      // await api.saveDesign(currentDesign);
    } catch (error) { }
  };

  const loadDesign = async (id: string) => {
    try {
      // API call to load design

      // const design = await api.loadDesign(id);
      // setCurrentDesign(design);
    } catch (error) { }
  };

  const exportDesign = async (format: 'png' | 'pdf', stageRef?: any) => {
    try {
      if (format === 'png' && stageRef?.current) {
        const stage = stageRef.current;
        const graniteConfig = (window as any).__appGraniteConfig;
        const designMode = (window as any).__appDesignMode || '';
        
        const dataURL = await renderProduction300DPI(
          stage,
          graniteConfig,
          designMode,
          currentDesign.elements,
          { format: 'png', quality: 1, targetDPI: 300 }
        );

        if (dataURL) {
          const link = document.createElement('a');
          link.download = `${currentDesign.name || 'memorial-design'}.png`;
          link.href = dataURL;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      }
    } catch (error) { }
  };

  const setIsTemplateSelected = (selected: boolean) => {
    // Only allow setting to true if we actually have template elements
    if (selected) {
      const hasTemplate = currentDesign.elements.some(el =>
        el.type === 'artwork' && (el.properties?.role === 'template' || el.properties?.isTemplate)
      );
      if (!hasTemplate) {
        return;
      }
    }
    setIsTemplateSelectedState(selected);
  };

  const setTemplateElement = (template: {
    src: string,
    width: number,
    height: number,
    colorId?: number,
    isFlatMarker?: boolean,
    extraProps?: any
  }) => {
    try { setLastChangeContext('model'); } catch { }
    setCurrentDesign(prev => {
      const colorId = template.colorId ?? undefined;
      // Remove ALL previous template/model elements
      let elements = prev.elements.filter(el => {
        const isTemplate = el.type === 'artwork' && (el.properties?.role === 'template' || el.properties?.isTemplate);
        const isVase = el.type === 'artwork' && (el.properties?.role === 'vase' || el.properties?.role === 'Vase' || el.properties?.kind === 'vase');
        return !(
          el.type === 'tablet' ||
          el.type === 'base' ||
          isTemplate ||
          isVase
        );
      });
      // Add new template element
      // Prefer the registered Konva stage/container size when available so templates
      // are centered inside the design canvas instead of the whole window. Fall back
      // to window dimensions when stageRef is not set.
      // Use the canonical logical design dimensions (1400x700) consistently
      // across all devices and modal states so elements stay centered and properly sized.
      let W = 1400;
      let H = 700;

      let scale: number;
      const padding = 60;
      const availW = Math.max(0, Math.floor(W - padding * 2));
      const availH = Math.max(0, Math.floor(H - padding * 2));
      scale = Math.min(availW / template.width, availH / template.height);
      const width = Math.floor(template.width * scale);
      const height = Math.floor(template.height * scale);

      const x = Math.floor((W - width) / 2);
      const y = Math.floor((H - height) / 2);
      const newElement: DesignElement = {
        id: `template-model-${Date.now()}`,
        type: 'artwork',
        x,
        y,
        width,
        height,
        rotation: 0,
        properties: {
          src: template.src,
          isTemplate: true,
          role: 'template',
          colorId: colorId,
          // store logical source dimensions so other logic (fit/resize) can use them
          logicalWidth: template.width,
          logicalHeight: template.height,
          isFlatMarker: Boolean(template.isFlatMarker === true),
          ...(template.extraProps || {})
        }
      };
      const finalElements = [...elements, newElement];
      // Automatically enable template selection when adding a template
      setIsTemplateSelectedState(true);
      const nextDesign = {
        ...prev,
        elements: finalElements
      } as Design;
      return nextDesign;
    });

    // Background is now handled by the auto-update useEffect based on model changes
  };

  const value: DesignContextType = {
    currentDesign,
    updateDesign: updateWithHistory,
    updateDesignNoHistory: updateWithoutHistory,
    lastChangeContext,
    undo,
    redo,
    canUndo: past.length > 0,
    canRedo: future.length > 0,
    selectedElement,
    setSelectedElement,
    currentTool,
    setCurrentTool,
    selectedTexture,
    setSelectedTexture,
    saveDesign,
    loadDesign,
    exportDesign,
    setSelectedModelId: (modelId: number | undefined) => {
      try { setLastChangeContext('model'); } catch { }
      setCurrentDesign(prev => ({ ...prev, selectedModelId: modelId }));
    },
    setSelectedModelName: (name: string | undefined) => {
      try { setLastChangeContext('model'); } catch { }
      setCurrentDesign(prev => ({ ...prev, selectedModelName: name }));
    },
    isTemplateSelected: isTemplateSelectedState,
    setIsTemplateSelected,
    stageRef,
    setStageRef,
    selectedBackground,
    setSelectedBackground,
    templateResetKey,
    incrementTemplateResetKey: () => setTemplateResetKey(prev => prev + 1),
    setTemplateElement,
    selectedGraniteImage,
    setSelectedGraniteImage,
    selectedGraniteBaseImage,
    setSelectedGraniteBaseImage,
    selectedGraniteMeta,
    setSelectedGraniteMeta,
    isApplyingDesign,
    setIsApplyingDesign,
    printSettings,
    setPrintSettings,
    layoutGuides,
    setLayoutGuides,
    isFlatMarkerCurrent: () => {
      try {
        const els = Array.isArray(currentDesign?.elements) ? currentDesign.elements : [];
        const tpl = els.find((el: any) => el?.properties?.isTemplate === true || String(el?.properties?.role || '').toLowerCase() === 'template');
        if (!tpl) return true; // initial state: treat as flat
        // If the property is explicitly set, respect it; otherwise default to flat
        if (Object.prototype.hasOwnProperty.call(tpl.properties || {}, 'isFlatMarker')) {
          return Boolean(tpl?.properties?.isFlatMarker === true);
        }
        return true;
      } catch {
        return true;
      }
    },
    isHistoryNavigating,
    setIsHistoryNavigating,
    isPanelPreloaderActive,
    setIsPanelPreloaderActive,
    activeSide: currentDesign.activeSide || 'front',
    setActiveSide: (side: 'front' | 'back') => {
      updateWithoutHistory(prev => ({ ...prev, activeSide: side }));
    },
    placementSlots,
    setPlacementSlots,
    activePlacementSlot,
    setActivePlacementSlot,
    pixelsPerInch,
    setPixelsPerInch,
    renderedTemplateOrigin,
    setRenderedTemplateOrigin,
    sampleGuideLines,
    setSampleGuideLines,
    designMode,
    setDesignMode: updateDesignMode,
    sharedModels,
    sharedColors,
    sharedCategories,
    sharedFonts,
    isGlobalLoading,
    setIsGlobalLoading,
    textStyles,
    isLoadingTextStyles,
    loadingMessage,
    setLoadingMessage,
    openModal,
    setOpenModal,
    showPersonalTextOnCanvas,
    setShowPersonalTextOnCanvas,
    authedUser,
    isDealerSalesperson: (() => {
      if (!authedUser) return false;
      const r = String(authedUser.role || authedUser.user_type_flag || authedUser.user_type || '').toLowerCase().trim();
      return r === 'dealer_salesperson' || r === 'dealer salesperson' || r.includes('salesperson');
    })(),
    canViewPrice: !(() => {
      if (!authedUser) return false;
      const r = String(authedUser.role || authedUser.user_type_flag || authedUser.user_type || '').toLowerCase().trim();
      return r === 'dealer_salesperson' || r === 'dealer salesperson' || r.includes('salesperson');
    })(),
    canCreateQuotation: !(() => {
      if (!authedUser) return false;
      const r = String(authedUser.role || authedUser.user_type_flag || authedUser.user_type || '').toLowerCase().trim();
      return r === 'dealer_salesperson' || r === 'dealer salesperson' || r.includes('salesperson');
    })(),
    setAuthedUser,
    loadTextAssets,
    areTextAssetsLoaded,
    loadArtworkCategories: useCallback(async () => {
      if (sharedCategories.length > 0) return;
      try {
        const res = await fetch(buildApiUrl('/api/art/categories'));
        if (res.ok) {
          const data = await res.json();
          const arr = Array.isArray(data.data) ? data.data : (Array.isArray(data) ? data : []);
          setSharedCategories(arr);
        }
      } catch (e) {
        console.error('Failed to load artwork categories on-demand:', e);
      }
    }, [sharedCategories]),
    loadShapesData: useCallback(async () => {
      if (sharedModels.length > 0) return;
      try {
        const res = await fetch(buildApiUrl('/api/models'));
        if (res.ok) {
          const data = await res.json();
          const arr = Array.isArray(data.data) ? data.data : (Array.isArray(data) ? data : []);
          setSharedModels(arr.sort((a: any, b: any) => (a.id || 0) - (b.id || 0)));
        }
      } catch (e) {
        console.error('Failed to load shape models on-demand:', e);
      }
    }, [sharedModels]),
    fetchModelDetails,
    fetchDesignPreview
  };

  // Initial design setup: Flat Marker first size by default for new designs
  React.useEffect(() => {
    // Only run if we are not loading a shared design and the design is currently empty
    // Wait for global loading (colors) to be done so sharedColors is populated
    if (isGlobalLoading || isApplyingDesign || (currentDesign as any)?.id || sharedColors.length === 0 || currentDesign.elements.length > 0) return;

    // Prevent double-initialization in Strict Mode
    if ((window as any).__initialDesignApplied) return;
    (window as any).__initialDesignApplied = true;

    const initDefaultDesign = async () => {
      try {
        if (designMode === 'ESQ') {
          // Default ESQ Flat Marker PJB
          let defaultModalId = 1;
          let defaultModalName = 'Flat Marker 24x12';
          let imgSrc = buildApiUrl('/api/uploads/esq_modals/Flat Markers/pjb/pjb_24x12.png');
          let defaultEsqBackground: any = {
            id: 0,
            name: 'Default Color',
            image_url: null,
            background_color: '#E0E0E0'
          };
          let defaultModalDims: { width_in?: any; height_in?: any; widthIn?: any; heightIn?: any } | null = null;

          // Dynamic color preservation for ESQ mode
          const activeColorId = Number(currentDesignRef.current?.selectedColorId || 5);
          const activeColorMeta = (sharedColors || []).find((c: any) => Number(c?.id) === activeColorId)
            || (sharedColors || []).find((c: any) => Number(c?.id) === 5)
            || null;
          const activeColorName = activeColorMeta?.name ? String(activeColorMeta.name) : 'Jet Black';

          // ESQ default should also honor modal background_path when available.
          try {
            const res = await fetch(buildApiUrl(`/api/esq/modals?color_id=${activeColorId}`));
            if (res.ok) {
              const modals = await res.json();
              const modalList = Array.isArray(modals) ? modals : [];
              const defaultModal = modalList.find((m: any) => Number(m?.id) === 1)
                || modalList.find((m: any) => String(m?.category || '').toLowerCase() === 'flat markers')
                || null;
              if (defaultModal) {
                defaultModalId = Number(defaultModal.id) || 1;
                defaultModalName = String(defaultModal.name || defaultModalName);
                defaultModalDims = defaultModal;
                const modalImgPath = String(defaultModal.image_path || '').trim();
                if (modalImgPath) {
                  imgSrc = buildApiUrl('/api/uploads/' + modalImgPath.replace(/^\/?uploads\//, ''));
                }
              }
              const bgPath = String(defaultModal?.background_path || '').trim();
              if (bgPath) {
                defaultEsqBackground = {
                  id: 1,
                  name: `${defaultModalName} Background`,
                  image_url: buildApiUrl('/api/uploads/' + bgPath.replace(/^\/?uploads\//, '')),
                  background_color: '#E0E0E0'
                };
              }
            }
          } catch { }

          updateWithoutHistory(prev => ({
            ...prev,
            selectedModelId: defaultModalId,
            selectedModelName: defaultModalName,
            selectedSizeId: 0,
            selectedColorName: activeColorName,
            selectedColorId: activeColorId,
            selectedColorIndex: sharedColors.findIndex(c => Number(c.id) === activeColorId)
          }));
          setSelectedBackground(defaultEsqBackground);

          setSelectedGraniteMeta({
            modelId: defaultModalId,
            sizeId: 0,
            colorId: activeColorId,
            src: imgSrc
          });

          setTemplateElement({
            src: imgSrc,
            width: 1200,
            height: 600,
            colorId: activeColorId,
            isFlatMarker: true,
            extraProps: {
              designMode: 'ESQ',
              esqModalId: defaultModalId,
              esqModalName: defaultModalName,
              esqModalWidthIn: (defaultModalDims as any)?.width_in ?? (defaultModalDims as any)?.widthIn,
              esqModalHeightIn: (defaultModalDims as any)?.height_in ?? (defaultModalDims as any)?.heightIn,
            }
          });
          return;
        }

        // Flat Marker (1) with active chosen color (or default 5)
        const modelId = 1;
        const colorId = Number(currentDesignRef.current?.selectedColorId || 5);
        const activeColorMeta = (sharedColors || []).find((c: any) => Number(c?.id) === colorId) || null;
        const activeColorName = activeColorMeta?.name ? String(activeColorMeta.name) : undefined;

        // Fetch real details to get the dynamic image path and size ID
        const data = await fetchModelDetails(modelId, colorId);
        const sizes = Array.isArray(data?.sizes) ? data.sizes : [];

        // Look for the size matching "24x12x4" specifically, or fallback to first
        const defaultSize = sizes.find((s: any) =>
          String(s.die_size || s.size_type || '').includes('24x12x4')
        ) || sizes[0];

        if (defaultSize) {
          const imgSrc = defaultSize.preview_image
            ? buildApiUrl(`/api/uploads/${String(defaultSize.preview_image).replace(/^\/?uploads\//, '')}`)
            : null;

          if (imgSrc) {
            // Update design state fields (model/size/color)
            const colorIndex = sharedColors.findIndex(c => Number(c.id) === colorId);
            updateWithoutHistory(prev => ({
              ...prev,
              selectedModelId: modelId,
              selectedModelName: data?.name || 'Flat Marker',
              selectedSizeId: defaultSize.id,
              selectedSizeName: String(defaultSize.die_size || defaultSize.size_type || ''),
              selectedColorId: colorId,
              ...(activeColorName ? { selectedColorName: activeColorName } : {}),
              selectedColorIndex: colorIndex >= 0 ? colorIndex : 0,
              porcelain_width_in: defaultSize.porcelain_width_in,
              porcelain_height_in: defaultSize.porcelain_height_in,
              porcelain_shape: defaultSize.porcelain_shape
            }));

            // Sync the granite metadata for the composite renderer
            setSelectedGraniteMeta({
              modelId,
              sizeId: defaultSize.id,
              colorId,
              src: imgSrc
            });

            // Helper to parse size from the image filename (e.g., 20x10.png)
            const parseSizeFromPath = (p?: string | null): { w: number; h: number } | null => {
              if (!p) return null;
              try {
                const parts = String(p).split('/').pop() || String(p);
                const m = parts.match(/([_-]?)(\d{1,4})[xX](\d{1,4})(?=\.|$)/);
                if (m) {
                  const w = Number(m[2]);
                  const h = Number(m[3]);
                  if (w > 0 && h > 0) return { w, h };
                }
              } catch (err) { }
              return null;
            };

            const logicalDims = parseSizeFromPath(defaultSize.preview_image || imgSrc);

            // Finally, add the template element to the canvas
            setTemplateElement({
              src: imgSrc,
              width: logicalDims ? logicalDims.w : (defaultSize.die_w || 600),
              height: logicalDims ? logicalDims.h : (defaultSize.die_h || 400),
              colorId: colorId,
              isFlatMarker: true
            });
          }
        }
      } catch (e) {
        console.error('Failed to initialize default design:', e);
      }
    };

    initDefaultDesign();
  }, [isGlobalLoading, isApplyingDesign, currentDesign.elements.length, sharedColors, fetchModelDetails, setTemplateElement, setSelectedGraniteMeta, updateWithoutHistory, setSelectedBackground]);

  return (
    <DesignContext.Provider value={value}>
      {children}
    </DesignContext.Provider>
  );
};

export const useDesignContext = (): DesignContextType => {
  const context = useContext(DesignContext);
  if (context === undefined) {
    return {} as any;
  }
  return context;
};